/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

export type MarkReadMode = 'read' | 'unread';

interface BatchMarkReadModalProps {
  modalOpened: boolean;
  setModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
  unreadCount: number;
  readCount: number;
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
  onConfirm: (mode: MarkReadMode) => void;
  onSuccess?: () => void;
}

const BatchMarkReadModal: React.FC<BatchMarkReadModalProps> = ({
  modalOpened,
  setModalOpened,
  unreadCount,
  readCount,
  isLoading,
  isLoaded,
  isError,
  onConfirm,
  onSuccess,
}) => {
  const [mode, setMode] = useState<MarkReadMode | ''>('');
  const [confirmedCount, setConfirmedCount] = useState<number>(0);

  const [actionDispatched, setActionDispatched] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const wasLoadingRef = useRef<boolean>(false);

  const isProcessing = actionDispatched && isLoading;

  const effectiveCount = mode === 'read' ? unreadCount : mode === 'unread' ? readCount : 0;
  const canSubmit = !!mode && effectiveCount > 0 && !isProcessing && !showSuccess;

  useEffect(() => {
    if (!actionDispatched) return;
    if (isLoading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;

    if (isLoaded) {
      setShowSuccess(true);
    } else if (isError) {
      setShowError(true);
    }
  }, [actionDispatched, isLoading, isLoaded, isError]);

  const handleConfirm = () => {
    if (!mode) return;
    setShowError(false);
    setConfirmedCount(mode === 'read' ? unreadCount : readCount);
    onConfirm(mode);
    setActionDispatched(true);
  };

  const closeModal = () => {
    if (showSuccess && onSuccess) onSuccess();
    setModalOpened(false);
  };

  const successLabel =
    mode === 'read'
      ? confirmedCount === 1
        ? '1 andamento marcado como lido!'
        : `${confirmedCount} andamentos marcados como lidos!`
      : confirmedCount === 1
      ? '1 andamento marcado como não lido!'
      : `${confirmedCount} andamentos marcados como não lidos!`;

  return (
    <Modal show={modalOpened} size="sm">
      <div className="modal-content">
        <div className="modal-header">
          <h6 className="modal-title">Marcar andamentos como</h6>
          <button aria-label="Close" className="close" type="button" onClick={closeModal}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-body">
          {showError && (
            <div className="my-2 alert alert-danger text-center">
              Erro ao alterar os andamentos.
            </div>
          )}
          {showSuccess && (
            <div className="my-2 alert alert-success text-center">{successLabel}</div>
          )}
          <div className="custom-control custom-radio mb-2">
            <input
              className="custom-control-input"
              type="radio"
              name="batch_mark_read_mode"
              id="batch-mark-read-option"
              checked={mode === 'read'}
              onChange={() => setMode('read')}
              disabled={unreadCount === 0}
            />
            <label className="custom-control-label" htmlFor="batch-mark-read-option">
              Lido{' '}
              <small className="text-muted">
                ({unreadCount} {unreadCount === 1 ? 'pendente' : 'pendentes'})
              </small>
            </label>
          </div>
          <div className="custom-control custom-radio mb-3">
            <input
              className="custom-control-input"
              type="radio"
              name="batch_mark_read_mode"
              id="batch-mark-unread-option"
              checked={mode === 'unread'}
              onChange={() => setMode('unread')}
              disabled={readCount === 0}
            />
            <label className="custom-control-label" htmlFor="batch-mark-unread-option">
              Não lido{' '}
              <small className="text-muted">
                ({readCount} {readCount === 1 ? 'pendente' : 'pendentes'})
              </small>
            </label>
          </div>
          {unreadCount === 0 && readCount === 0 && (
            <div className="alert alert-warning mb-0" role="alert">
              Selecione pelo menos um andamento.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-md btn-secondary"
            disabled={isProcessing}
            onClick={closeModal}
          >
            {showSuccess ? 'Fechar' : 'Cancelar'}
          </button>
          {!showSuccess && (
            <button
              type="button"
              className="btn btn-md btn-primary"
              disabled={!canSubmit}
              onClick={handleConfirm}
            >
              Aplicar
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BatchMarkReadModal;
